import React, { useState, useRef } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { Phone, MessageCircle, X, GripVertical } from 'lucide-react';
import { buildWaUrl, formatPhoneTel } from '../../lib/contactUtils.ts';

export default function FloatingContactWidget() {
  const { settings, showToast } = useApp();
  const [showTooltip, setShowTooltip] = useState(true);

  // Position & Pointer Drag State
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number; posX: number; posY: number }>({
    x: 0,
    y: 0,
    posX: 0,
    posY: 0,
  });

  const config = settings?.contactWidget || {
    enabled: true,
    whatsappNumber: settings?.contact?.whatsappNumber || '6282130955059',
    phoneNumber: settings?.contact?.phone || '+6287824918212',
    whatsappLabel: 'Konsultasi WhatsApp',
    phoneLabel: 'Telepon Kantor',
    statusBadgeText: '🟢 Online 24 Jam | Siap Melayani',
    defaultWaMessage:
      'Halo Tim Advokat ALY LAW FIRM, saya ingin berkonsultasi mengenai permasalahan hukum.',
    popupGreeting: 'Butuh konsultasi atau pendampingan hukum darurat? Hubungi tim advokat kami langsung!',
    showPhone: true,
    showWhatsapp: true,
  };

  if (config.enabled === false) {
    return null;
  }

  const activeWaNumber = config.whatsappNumber || settings?.contact?.whatsappNumber || '6281234567890';
  const activePhoneNumber = config.phoneNumber || settings?.contact?.phone || '+62 812-3456-7890';

  const waUrl = buildWaUrl(activeWaNumber, config.defaultWaMessage);
  const phoneTel = formatPhoneTel(activePhoneNumber);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      posX: position.x,
      posY: position.y,
    };
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch (err) {}
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setPosition({
      x: dragStartRef.current.posX + dx,
      y: dragStartRef.current.posY + dy,
    });
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (isDragging) {
      setIsDragging(false);
      try {
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      } catch (err) {}
    }
  };

  const handlePhoneClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Attempt to copy number to clipboard on desktop for convenience
    if (navigator.clipboard) {
      navigator.clipboard.writeText(activePhoneNumber).catch(() => {});
    }
    showToast(`Calling: ${activePhoneNumber} (Number copied to clipboard)`, 'info');
  };

  return (
    <div
      id="floating-contact-widget"
      style={{
        transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
      }}
      className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex flex-col items-end gap-2 max-w-[95vw] sm:max-w-sm pointer-events-auto select-none transition-shadow ${
        isDragging ? 'opacity-90 scale-102' : ''
      }`}
    >
      {/* Floating Status / Greeting Tooltip Banner */}
      {showTooltip && (
        <div className="relative bg-slate-950/95 backdrop-blur-md text-white px-3 py-1.5 sm:px-4 sm:py-2.5 rounded-xl sm:rounded-2xl shadow-2xl border border-emerald-500/30 flex items-center gap-2 max-w-[85vw] sm:max-w-sm animate-in fade-in slide-in-from-bottom-3 duration-300">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-ping shrink-0" />
          <div className="flex-1 pr-2">
            <p className="text-[10px] sm:text-xs text-slate-200 font-medium leading-tight">
              {config.popupGreeting || 'Need quick sustainability advisory or proposal? Contact us directly!'}
            </p>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowTooltip(false);
            }}
            aria-label="Close notification"
            className="text-slate-400 hover:text-white p-0.5 rounded-md hover:bg-slate-800 transition-colors cursor-pointer shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Main Attention-Grabbing Action Stack - Row format for all sizes */}
      <div className="flex flex-row items-center gap-1.5 sm:gap-2 bg-white/95 backdrop-blur-md p-1 sm:p-1.5 rounded-full shadow-2xl border-2 border-emerald-500/40 ring-4 ring-emerald-500/10 transition-all hover:shadow-emerald-500/20">
        {/* Dedicated Drag Grip Icon Handle - Pointer capture ONLY attached here */}
        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          className="flex items-center justify-center py-1 sm:py-1.5 px-2.5 sm:px-1.5 text-slate-400 hover:text-slate-700 cursor-grab active:cursor-grabbing touch-none select-none rounded-full bg-slate-50/80 sm:bg-transparent hover:bg-slate-100 transition-colors shrink-0"
          title="Drag to reposition widget"
        >
          <GripVertical className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-500" />
          <span className="text-[9px] font-black tracking-wider text-slate-500 uppercase sm:hidden ml-1 shrink-0">Drag</span>
        </div>

        {/* WhatsApp Button */}
        {config.showWhatsapp !== false && (
          <a
            id="floating-wa-btn"
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="relative flex items-center justify-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2.5 rounded-full bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-extrabold text-[10px] sm:text-xs shadow-md hover:shadow-lg hover:brightness-105 active:scale-95 transition-all cursor-pointer shrink-0"
          >
            <MessageCircle className="w-3.5 h-3.5 fill-white text-white shrink-0" />
            <span className="whitespace-nowrap tracking-tight">
              <span className="hidden sm:inline">{config.whatsappLabel || 'WhatsApp Advisory'}</span>
              <span className="inline sm:hidden">WA</span>
            </span>
          </a>
        )}

        {/* Direct Phone Call Button */}
        {config.showPhone !== false && (
          <a
            id="floating-phone-btn"
            href={`tel:${phoneTel}`}
            onClick={handlePhoneClick}
            className="flex items-center justify-center gap-1 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-[10px] sm:text-xs shadow-xs hover:shadow-md active:scale-95 transition-all cursor-pointer shrink-0"
            title={`Direct Phone: ${activePhoneNumber}`}
          >
            <Phone className="w-3 h-3 text-emerald-400 shrink-0" />
            <span className="whitespace-nowrap text-slate-100 tracking-tight">
              <span className="hidden sm:inline">{config.phoneLabel || 'Phone'}</span>
              <span className="inline sm:hidden">Tel</span>
            </span>
          </a>
        )}
      </div>
    </div>
  );
}
